import { useState, useId } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UserSelect,
  LocationSelect,
  RegionSelect,
  CompanySelect,
  OrganizationSelect,
} from "@/components/common/entity-select";
import { userApi } from "@/lib/api/services/user.api";
import type { Role } from "@/lib/api/admin-api";

type ScopeType = "ORGANIZATION" | "COMPANY" | "REGION" | "LOCATION";

interface AddScopeDialogProps {
  role: Role;
  open: boolean;
  onOpenChange(v: boolean): void;
  onSuccess(): void;
}

const SCOPE_TYPE_LABELS: Record<ScopeType, MessageDescriptor> = {
  ORGANIZATION: msg`Organization`,
  // The scope key stays COMPANY — the model rename is a separate change — but
  // the word users read is Concept (T-106).
  COMPANY: msg`Concept`,
  REGION: msg`Region`,
  LOCATION: msg`Location`,
};

export function AddScopeDialog({
  role,
  open,
  onOpenChange,
  onSuccess,
}: AddScopeDialogProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const idPrefix = useId();

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [scopeType, setScopeType] = useState<ScopeType | "">("");
  const [scopeId, setScopeId] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      if (!selectedUserId || !scopeType || !scopeId) throw new Error("Incomplete");
      return userApi.addScope(selectedUserId, {
        roleId: Number(role.id),
        scopeType,
        scopeId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles", role.id, "users"] });
      queryClient.invalidateQueries({ queryKey: ["roles", "scopes"] });
      toast.success(t`Scope assignment added`);
      resetForm();
      onOpenChange(false);
      onSuccess();
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Failed to add scope assignment`);
    },
  });

  const resetForm = () => {
    setSelectedUserId(null);
    setScopeType("");
    setScopeId(null);
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) resetForm();
    onOpenChange(v);
  };

  const isValid = selectedUserId !== null && scopeType !== "" && scopeId !== null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogTitle>{t`Add Scope Assignment`}</DialogTitle>
        <p className="text-sm text-(--color-text-secondary) -mt-2">
          {t`Grant role`} <span className="font-medium">{role.name}</span> {t`to a user at a specific scope.`}
        </p>

        <div className="flex flex-col gap-4 pt-2">
          {/* User */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-user`}>{t`User`}</Label>
            <UserSelect
              value={selectedUserId}
              onChange={(v) => setSelectedUserId(v)}
              onClear={() => setSelectedUserId(null)}
            />
          </div>

          {/* Scope Type */}
          <div className="flex flex-col gap-1.5">
            <Label>{t`Scope Type`}</Label>
            <Select
              value={scopeType}
              onValueChange={(v) => {
                setScopeType(v as ScopeType);
                setScopeId(null);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t`Select scope type…`} />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(SCOPE_TYPE_LABELS) as ScopeType[]).map((st) => (
                  <SelectItem key={st} value={st}>
                    {t(SCOPE_TYPE_LABELS[st])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scope Entity */}
          {scopeType && (
            <div className="flex flex-col gap-1.5">
              <Label>{t(SCOPE_TYPE_LABELS[scopeType])}</Label>
              {scopeType === "LOCATION" && (
                <LocationSelect
                  value={scopeId}
                  onChange={(v) => setScopeId(v)}
                  placeholder={t`Select location…`}
                />
              )}
              {scopeType === "REGION" && (
                <RegionSelect
                  value={scopeId}
                  onChange={(v) => setScopeId(v)}
                  placeholder={t`Select region…`}
                />
              )}
              {scopeType === "COMPANY" && (
                <CompanySelect
                  value={scopeId}
                  onChange={(v) => setScopeId(v)}
                  placeholder={t`Select concept…`}
                />
              )}
              {scopeType === "ORGANIZATION" && (
                <OrganizationSelect
                  value={scopeId}
                  onChange={(v) => setScopeId(v)}
                  adminScope
                  placeholder={t`Select organization…`}
                />
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <DialogClose asChild>
            <Button variant="outline">{t`Cancel`}</Button>
          </DialogClose>
          <Button
            disabled={!isValid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t`Add assignment`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
